import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, distinctUntilChanged, map } from 'rxjs';
import { CelebrusDataLayer } from '@core/models/celebrus-data-layer.model';
import { getUser } from '@core/store/entities/user/user.selector';
import {
  getSelectedProductsIncludingNWXInactive,
} from '@core/store/entities/product/product.selectors';
import {
  readCelebrusDataLayer,
  toCelebrusQuote,
  writeCelebrusDataLayer,
} from '@core/utils/celebrus-data-layer.util';

@Injectable({
  providedIn: 'root',
})
export class CelebrusDataLayerService {
  private initialized = false;

  constructor(
    private store: Store,
    private window: Window
  ) {}

  initialize(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    combineLatest([
      this.store.select(getUser),
      this.store.select(getSelectedProductsIncludingNWXInactive),
    ])
      .pipe(
        map(([user, products]): CelebrusDataLayer => {
          const existingDataLayer = this.readExistingDataLayer();
          const role = user.role || existingDataLayer.roles[0];
          const quotes = products.flatMap((product) =>
            product.quoteId
              ? [toCelebrusQuote(product.type, product.quoteId)]
              : []
          );

          return {
            ...(user.userId || existingDataLayer.userID
              ? { userID: user.userId || existingDataLayer.userID }
              : {}),
            roles: role ? [role] : [],
            quotes,
          };
        }),
        distinctUntilChanged(
          (previous, current) =>
            JSON.stringify(previous) === JSON.stringify(current)
        )
      )
      .subscribe((dataLayer) => {
        writeCelebrusDataLayer(this.window.sessionStorage, dataLayer);
      });
  }

  private readExistingDataLayer(): CelebrusDataLayer {
    try {
      return readCelebrusDataLayer(this.window.sessionStorage);
    } catch (error) {
      console.error('Unable to parse the Celebrus data layer', error);
      return { roles: [], quotes: [] };
    }
  }
}
